'use client';

import { useCookieConsent } from '@/components/client/cookie-consent/cookie-consent-hooks';
import { cn } from '@/lib/utils';
import React, { useState } from 'react';
import CookieSettingsModal from './cookie-settings-modal';
import { Settings } from 'lucide-react';

type CookieNoticeProps = {
  children: React.ReactNode;
  className?: string;
  debug?: boolean;
};

const emptySubscribe = () => () => {};

export default function CookieNotice({
  children,
  className,
  debug = false,
}: CookieNoticeProps) {
  const { consentCategories } = useCookieConsent();

  // Use useSyncExternalStore to avoid hydration mismatch
  // Server always returns false, client returns actual visibility state
  const shouldShowBanner = React.useSyncExternalStore(
    emptySubscribe, // subscribe: no external store, so empty function
    () => consentCategories === null || debug, // getSnapshot: client-side value
    () => false, // getServerSnapshot: server-side value (always false to avoid hydration mismatch)
  );

  if (!shouldShowBanner) return null;

  return (
    <div
      className={cn(
        'relative inset-x-0 bottom-0 flex flex-col justify-between gap-x-8 gap-y-4 bg-white p-6 ring-1 ring-gray-900/10 md:flex-row md:items-center lg:px-8',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function AcceptAllButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { acceptAll } = useCookieConsent();

  return (
    <button
      className={cn(
        'rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900',
        className,
      )}
      onClick={acceptAll}
      type="button"
    >
      {children}
    </button>
  );
}

export function AcceptRequiredButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { acceptRequired } = useCookieConsent();

  return (
    <button
      className={cn('text-sm font-semibold leading-6 text-gray-900', className)}
      onClick={acceptRequired}
      type="button"
    >
      {children}
    </button>
  );
}

export function RejectAllButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { rejectAll } = useCookieConsent();

  return (
    <button
      className={cn('text-sm font-semibold leading-6 text-gray-900', className)}
      onClick={rejectAll}
      type="button"
    >
      {children}
    </button>
  );
}

export function CustomizeIconButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <button
        className={cn(
          'inline-flex items-center justify-center rounded-md bg-transparent px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500',
          className,
        )}
        onClick={() => setModalOpen(true)}
        type="button"
        title="Cookie settings"
        aria-label="Open cookie settings"
      >
        <Settings className="mr-2 h-4 w-4" />
        <span>{children}</span>
      </button>
      <CookieSettingsModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}

export function CustomizeTextButton({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <button
        className={cn(
          'text-sm leading-6 text-gray-900 hover:text-gray-700',
          className,
        )}
        onClick={() => setModalOpen(true)}
        type="button"
        title="Cookie settings"
        aria-label="Open cookie settings"
      >
        {children}
      </button>
      <CookieSettingsModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
